import React from 'react';
import { Download, X } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import Typography from '@/components/ui/typography';
import ProcessingIndicator from './ProcessingIndicator';
import type { BatchCheckItem } from './types';

interface BatchCheckCardProps {
  check: BatchCheckItem;
  onDownload: (id: string) => void;
  onCancel?: (id: string) => void;
  isCancelling?: boolean;
}

const BatchCheckCard: React.FC<BatchCheckCardProps> = ({
  check,
  onDownload,
  onCancel,
  isCancelling = false,
}) => {
  const showActionButton =
    check.status === 'COMPLETED' ||
    check.status === 'IN_PROGRESS' ||
    check.status === 'SCHEDULED';
  const showCancelButton =
    (check.status === 'IN_PROGRESS' || check.status === 'SCHEDULED') &&
    onCancel !== undefined;

  return (
    <Card className="bg-white border border-gray-200 rounded-lg p-6 relative">
      {/* Action Buttons - Download for COMPLETED, Cancel for IN_PROGRESS */}
      {showActionButton && (
        <div className="absolute top-6 right-6 flex items-center gap-2">
          {check.status === 'COMPLETED' && (
            <button
              onClick={() => onDownload(check.id)}
              className="p-1 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
              aria-label="Download"
            >
              <Download className="h-5 w-5" />
            </button>
          )}
          {showCancelButton && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onCancel(check.id)}
              disabled={isCancelling}
              className="h-8 px-3 text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
            >
              <X className="h-4 w-4 mr-1" />
              Cancel
            </Button>
          )}
        </div>
      )}

      {/* Title */}
      <Typography
        variant="h2"
        color="neutral900"
        weight="semibold"
        className={`mb-4 ${showActionButton ? 'pr-24' : ''}`}
        size="18"
      >
        {check.title}
      </Typography>

      {/* Status Display */}
      <div className="mb-4">
        {check.status === 'IN_PROGRESS' ? (
          <ProcessingIndicator />
        ) : (
          <Typography
            variant="p"
            weight="medium"
            size="14"
            className={
              check.status === 'COMPLETED'
                ? 'text-[#5536F5]'
                : check.status === 'FAILED'
                  ? 'text-red-600'
                  : check.status === 'CANCELLED'
                    ? 'text-gray-600'
                    : 'text-gray-500'
            }
          >
            {check.status === 'COMPLETED'
              ? 'Completed'
              : check.status === 'FAILED'
                ? 'Failed'
                : check.status === 'CANCELLED'
                  ? 'Cancelled'
                  : check.status === 'REGISTERED'
                    ? 'Registered'
                    : check.status}
          </Typography>
        )}
      </div>

      <div className="flex items-start justify-between gap-4">
        {/* Completion Status */}
        <div className="text-gray-700 bg-gray-100 rounded-md py-1 px-2 shrink-0 max-w-[min(100%,28rem)]">
          <Typography size="14" className="leading-snug">
            {check.success > 0 || check.failed > 0 || check.skipped > 0
              ? `${check.success} success · ${check.failed} failed · ${check.skipped} skipped · ${check.total} total`
              : check.completionStatus}
          </Typography>
        </div>

        {(check.createdDate || check.completedDate) && (
          <div className="flex min-w-0 flex-col items-end gap-1 text-right">
            {check.createdDate && (
              <Typography variant="small" color="neutral500" size="12">
                Created on {check.createdDate} {check.createdTime}
              </Typography>
            )}
            {check.completedDate && (
              <Typography variant="small" color="neutral500" size="12">
                {check.status === 'COMPLETED'
                  ? `Check completed on ${check.completedDate} ${check.completedTime}`
                  : check.status === 'FAILED'
                    ? `Failed on ${check.completedDate} ${check.completedTime}`
                    : check.status === 'CANCELLED'
                      ? `Cancelled on ${check.completedDate} ${check.completedTime}`
                      : `Last updated on ${check.completedDate} ${check.completedTime}`}
              </Typography>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};

export default BatchCheckCard;
